// client/src/pages/pmPortal/OwnerProjectionsPage.js
// A projection is not a budget. A budget is committed and variance is measured against
// it; a projection carries stated assumptions and is expected to be wrong. The
// assumption is shown beside every figure for exactly that reason — a five-year number
// with no reasoning attached gets read as a promise.
import React, { useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Table, Loading, Notice, Select, money, titleCase }
    from '../../components/secure/pm/PMKit';

const thisYear = new Date().getFullYear();
const YEARS = [thisYear, thisYear - 1, thisYear - 2];

const OwnerProjectionsPage = () => {
    const api = usePmPortalApiClient();
    const [baseYear, setBaseYear] = useState(thisYear);
    const [rows, setRows] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        setRows(null);
        setError('');
        api.get(`/api/pm-portal/owner/projection/${baseYear}`)
            .then(setRows)
            .catch(e => setError(e.message));
    }, [api, baseYear]);

    return (
        <>
            <PageHeader
                title="Five-year projection"
                subtitle="Estimates, not commitments. Each figure carries the assumption it rests on."
            />

            <Panel title="Base year">
                <Select
                    label="Projected from"
                    value={baseYear}
                    onChange={e => setBaseYear(Number(e.target.value))}
                    options={YEARS.map(y => ({ value: y, label: String(y) }))}
                    className="max-w-xs"
                />
            </Panel>

            {error && <Notice tone="bad" title="Could not load the projection" message={error} />}
            {!rows && !error && <Loading />}

            {rows && (
                <Panel title={`Projection from ${baseYear}`}>
                    <Table
                        columns={[
                            { key: 'year_offset', label: 'Year', render: r => `${baseYear + Number(r.year_offset)}` },
                            { key: 'summary_line', label: 'Line', render: r => r.name || titleCase(r.summary_line) },
                            { key: 'amount', label: 'Projected', align: 'right', render: r => money(r.amount) },
                            {
                                key: 'growth_rate', label: 'Growth', align: 'right',
                                render: r => (r.growth_rate === null || r.growth_rate === undefined
                                    ? '—' : `${Number(r.growth_rate).toFixed(2)}%`),
                            },
                            {
                                key: 'assumption', label: 'Assumption',
                                render: r => <span className="text-slate-600">{r.assumption || '—'}</span>,
                            },
                        ]}
                        rows={rows}
                        empty={`No projection has been prepared from ${baseYear}.`}
                    />
                </Panel>
            )}
        </>
    );
};

export default OwnerProjectionsPage;
